<template>
  <v-card class="mb-3" outlined>
    <v-card-text>
      <div class="d-flex align-start">
        <v-checkbox
          v-if="showSelect && !isReviewMode"
          :input-value="selected"
          :disabled="busy || !hasEligible"
          class="mt-0 pt-0 mr-2"
          hide-details
          @change="$emit('toggle-select')"
        />
        <div class="flex-grow-1">
          <div class="d-flex align-center flex-wrap" style="gap: 8px">
            <v-chip small color="deep-purple" dark>{{ actionTypeLabel }}</v-chip>
            <v-chip v-if="isReviewMode" small :color="statusColor" dark>{{ statusLabel }}</v-chip>
            <support-request-ticket-chip :ticket-id="action.supportRequest.hubspotTicketId" />
            <support-request-school-chip
              :school-id="action.supportRequest.resolvedSchoolId"
              :school-name="action.supportRequest.resolvedSchoolName"
            />
            <div class="grey--text text-caption text-right ml-auto">
              <div v-if="formattedTicketCreatedAt">
                {{ $t('supportRequests.card.ticket_created_at', {
                  date: formattedTicketCreatedAt,
                }) }}
              </div>
              <div>{{ $t('supportRequests.card.proposed_at', { date: formattedCreatedAt }) }}</div>
            </div>
          </div>

          <div v-if="action.supportRequest.subject" class="font-weight-bold mt-2">
            {{ action.supportRequest.subject }}
          </div>

          <v-alert v-if="action.evidence" dense text type="info" class="mt-3 mb-2">
            <div class="text-caption font-weight-bold mb-1">
              {{ $t('supportRequests.card.evidence_title') }}
            </div>
            <div style="white-space: pre-wrap">{{ action.evidence }}</div>
          </v-alert>

          <div v-if="action.supportRequest.content" class="mb-2">
            <v-btn text x-small color="deep-purple" @click="showFullTicket = !showFullTicket">
              {{ showFullTicket
                ? $t('supportRequests.card.hide_full_ticket')
                : $t('supportRequests.card.view_full_ticket') }}
            </v-btn>
            <div v-if="showFullTicket" class="text-body-2 mt-1" style="white-space: pre-wrap">
              {{ action.supportRequest.content }}
            </div>
          </div>

          <v-alert v-if="action.approvalNote" dense text type="success" class="mb-2">
            <div class="text-caption font-weight-bold mb-1">
              {{ $t('supportRequests.card.approval_note_title') }}
            </div>
            {{ action.approvalNote }}
          </v-alert>

          <v-alert v-if="action.diagnosis" dense text type="info" class="mb-2">
            <div class="text-caption font-weight-bold mb-1">
              {{ $t('supportRequests.card.diagnosis_title') }}
            </div>
            <div style="white-space: pre-wrap">{{ action.diagnosis }}</div>
          </v-alert>

          <v-alert v-if="action.manualReason" dense text type="warning" class="mb-2">
            <div class="text-caption font-weight-bold mb-1">
              {{ $t('supportRequests.card.manual_reason_title') }}
            </div>
            {{ action.manualReason }}
          </v-alert>

          <v-alert v-if="action.executionWarning" dense text type="warning" class="mb-2">
            <div class="text-caption font-weight-bold mb-1">
              {{ $t('supportRequests.card.execution_warning_title') }}
            </div>
            <div style="white-space: pre-wrap">{{ action.executionWarning }}</div>
          </v-alert>

          <div v-if="action.supportRequest.replyDraft !== null" class="mb-2">
            <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">
              {{ $t('supportRequests.card.reply_draft_title') }}
            </div>
            <v-textarea
              v-model="draftText"
              outlined
              dense
              rows="3"
              auto-grow
              hide-details
              :disabled="savingDraft"
            />
            <div class="d-flex justify-end mt-1">
              <v-btn
                small
                text
                color="deep-purple"
                :disabled="savingDraft || draftText === action.supportRequest.replyDraft"
                :loading="savingDraft"
                @click="onSaveDraft"
              >
                {{ $t('supportRequests.card.save_draft') }}
              </v-btn>
            </div>
          </div>

          <div v-if="changeRows.length" class="mb-2">
            <div class="text-caption font-weight-bold grey--text text--darken-1">
              {{ $t('supportRequests.card.changes_title') }}
            </div>
            <div v-for="row in changeRows" :key="row.field" class="text-body-2">
              <strong>{{ row.label }}:</strong> {{ row.from }} → {{ row.to }}
            </div>
          </div>
          <div v-else-if="payloadRows.length" class="mb-2">
            <div class="text-caption font-weight-bold grey--text text--darken-1">
              {{ $t('supportRequests.card.payload_title') }}
            </div>
            <div v-for="row in payloadRows" :key="row.key" class="text-body-2">
              <strong>{{ row.label }}:</strong> {{ row.value }}
            </div>
          </div>

          <div v-if="action.targets.length" class="mt-3">
            <div class="text-caption font-weight-bold grey--text text--darken-1 mb-1">
              {{ $t('supportRequests.card.targets_title') }}
            </div>

            <div
              v-for="target in action.targets"
              :key="target.id"
              class="d-flex align-start flex-wrap py-1"
              style="gap: 8px"
            >
              <v-checkbox
                v-if="!isReviewMode && isEligible(target)"
                :input-value="checkedTargetIds.includes(target.id)"
                :disabled="busy"
                class="mt-0 pt-0"
                hide-details
                @change="toggleTarget(target.id)"
              />
              <v-icon v-else small class="mt-1">mdi-minus</v-icon>

              <router-link
                v-if="targetProfileRoute(target.bookuserId)"
                :to="targetProfileRoute(target.bookuserId)"
                target="_blank"
              >
                {{ target.rawName || '—' }}
              </router-link>
              <span v-else>{{ target.rawName || '—' }}</span>

              <v-chip x-small :color="resolutionColor(target.resolution)" dark>
                {{ resolutionLabel(target.resolution) }}
              </v-chip>
              <span v-if="target.status !== 'PENDING'" class="grey--text text-caption">
                ({{ targetStatusLabel(target.status) }})
              </span>

              <template
                v-if="!isReviewMode
                  && target.resolution === 'AMBIGUOUS'
                  && target.candidates.length"
              >
                <div class="flex-grow-1" style="min-width: 260px">
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    {{ $t('supportRequests.card.candidates_label') }}
                  </div>
                  <v-radio-group
                    :value="pickChoice[target.id]"
                    hide-details
                    dense
                    class="mt-0 pt-0"
                    :disabled="busy"
                    @change="$set(pickChoice, target.id, $event)"
                  >
                    <div
                      v-for="candidate in target.candidates"
                      :key="candidate.bookuserId"
                      class="d-flex align-center flex-wrap py-1"
                      style="gap: 6px"
                    >
                      <v-radio :value="candidate.bookuserId" dense hide-details />
                      <span>{{ candidate.fullName }}</span>
                      <span class="grey--text text-caption">
                        DNI {{ candidate.dni || 's/d' }} — {{ candidate.course || 'sin curso' }}
                        — Nac. {{ birthDateLabel(candidate.birthDate) }}
                      </span>
                      <router-link
                        v-if="targetProfileRoute(candidate.bookuserId)"
                        :to="targetProfileRoute(candidate.bookuserId)"
                        target="_blank"
                        class="text-caption"
                      >
                        {{ $t('supportRequests.card.view_profile') }}
                      </router-link>
                    </div>
                  </v-radio-group>
                  <v-btn
                    small
                    outlined
                    color="deep-purple"
                    class="mt-1"
                    :disabled="busy || !pickChoice[target.id]"
                    @click="onPick(target)"
                  >
                    {{ $t('supportRequests.card.pick') }}
                  </v-btn>
                </div>
              </template>
            </div>
          </div>

          <div v-if="!isReviewMode" class="mt-3 d-flex" style="gap: 8px">
            <v-tooltip top :disabled="!approveDisabled">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    small
                    color="deep-purple"
                    dark
                    :disabled="approveDisabled"
                    :loading="busy"
                    @click="onApprove"
                  >
                    {{ approveLabel }}
                  </v-btn>
                </span>
              </template>
              <span>{{ approveDisabledReason }}</span>
            </v-tooltip>
            <v-btn small outlined color="red" :disabled="busy" @click="$emit('reject')">
              {{ $t('supportRequests.card.reject') }}
            </v-btn>
          </div>
          <div v-if="!isReviewMode && !hasEligible" class="grey--text text-caption mt-1">
            {{ $t('supportRequests.card.no_eligible_targets') }}
          </div>

          <div v-if="isReviewMode" class="mt-3">
            <div v-if="action.undoneAt" class="grey--text text-caption">
              {{ $t('supportRequests.card.undone_by', {
                by: action.undoneBy, date: formattedUndoneAt,
              }) }}
            </div>
            <v-btn
              v-else-if="action.status === 'EXECUTED' || action.status === 'EXECUTED_WITH_WARNING'"
              small
              outlined
              color="red"
              :disabled="busy"
              :loading="busy"
              @click="$emit('undo')"
            >
              {{ $t('supportRequests.card.undo') }}
            </v-btn>
          </div>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import supportRequestService from '@/services/supportRequest.service';
import snackbarMixin from '@/mixins/snackbar';
import {
  mapChangesToRows, canApproveInLane, userProfileRoute, formatBirthDate,
} from '@/utils/supportRequests';
import SupportRequestTicketChip from './SupportRequestTicketChip.vue';
import SupportRequestSchoolChip from './SupportRequestSchoolChip.vue';

const ELIGIBLE_RESOLUTIONS = ['RESOLVED_UNIQUE', 'RESOLVED_FUZZY', 'PICKED_BY_HUMAN'];

const RESOLUTION_COLORS = {
  RESOLVED_UNIQUE: 'green',
  RESOLVED_FUZZY: 'light-green darken-1',
  PICKED_BY_HUMAN: 'teal',
  AMBIGUOUS: 'orange',
  NOT_FOUND: 'grey',
  DNI_NAME_CONFLICT: 'red',
};

const STATUS_COLORS = {
  EXECUTED: 'green',
  EXECUTED_WITH_WARNING: 'orange',
  REJECTED: 'red',
  FAILED: 'red darken-1',
};

export default {
  name: 'SupportRequestActionCard',
  components: { SupportRequestTicketChip, SupportRequestSchoolChip },
  mixins: [snackbarMixin],
  props: {
    action: { type: Object, required: true },
    showSelect: { type: Boolean, default: false },
    selected: { type: Boolean, default: false },
    busy: { type: Boolean, default: false },
  },
  data: () => ({
    checkedTargetIds: [],
    pickChoice: {},
    draftText: '',
    savingDraft: false,
    showFullTicket: false,
  }),
  computed: {
    actionTypeLabel() {
      return this.$t(`supportRequests.action_types.${this.action.actionType}`);
    },
    formattedCreatedAt() {
      return new Date(this.action.createdAt).toLocaleString();
    },
    formattedTicketCreatedAt() {
      const { ticketCreatedAt } = this.action.supportRequest;
      return ticketCreatedAt ? new Date(ticketCreatedAt).toLocaleString() : null;
    },
    formattedUndoneAt() {
      return this.action.undoneAt ? new Date(this.action.undoneAt).toLocaleString() : '';
    },
    isReviewMode() {
      return this.action.status !== 'PROPOSED';
    },
    statusColor() {
      return STATUS_COLORS[this.action.status] || 'grey';
    },
    statusLabel() {
      const key = `supportRequests.action_status.${this.action.status}`;
      return this.$te(key) ? this.$t(key) : this.action.status;
    },
    approveLabel() {
      return this.allEligibleChecked
        ? this.$t('supportRequests.card.approve')
        : this.$t('supportRequests.card.approve_partial');
    },
    changeRows() {
      return mapChangesToRows(
        this.action.changes,
        (field) => this.$t(`supportRequests.change_fields.${field}`),
        this.$t('supportRequests.card.changes_empty_value'),
      );
    },
    payloadRows() {
      const { payload } = this.action;
      if (!payload || typeof payload !== 'object') return [];
      return Object.entries(payload)
        .filter(([, value]) => value !== null && value !== undefined && value !== '')
        .map(([key, value]) => ({ key, label: this.humanizeKey(key), value: String(value) }));
    },
    eligibleIds() {
      return this.action.targets.filter((t) => this.isEligible(t)).map((t) => t.id);
    },
    hasEligible() {
      return this.eligibleIds.length > 0;
    },
    allEligibleChecked() {
      return (
        this.checkedTargetIds.length === this.eligibleIds.length
        && this.eligibleIds.every((id) => this.checkedTargetIds.includes(id))
      );
    },
    // Una gestión del carril Manual (o con manualReason) no puede aprobarse
    // con un click: hay que revisarla a mano primero (KB-907).
    canApprove() {
      return canApproveInLane(this.action);
    },
    approveDisabled() {
      return this.busy || !this.hasEligible || !this.canApprove;
    },
    approveDisabledReason() {
      if (!this.canApprove) return this.$t('supportRequests.card.approve_disabled_manual');
      if (!this.hasEligible) return this.$t('supportRequests.card.no_eligible_targets');
      return '';
    },
  },
  watch: {
    'action.id': {
      immediate: true,
      handler() {
        this.checkedTargetIds = [...this.eligibleIds];
        this.pickChoice = {};
        this.draftText = this.action.supportRequest.replyDraft ?? '';
        this.showFullTicket = false;
      },
    },
    'action.version': function onVersionChange() {
      this.checkedTargetIds = [...this.eligibleIds];
    },
  },
  methods: {
    isEligible(target) {
      return target.status === 'PENDING' && ELIGIBLE_RESOLUTIONS.includes(target.resolution);
    },
    resolutionColor(resolution) {
      return RESOLUTION_COLORS[resolution] || 'grey';
    },
    resolutionLabel(resolution) {
      return this.$t(`supportRequests.resolutions.${resolution}`);
    },
    targetStatusLabel(status) {
      return this.$t(`supportRequests.target_status.${status}`);
    },
    humanizeKey(key) {
      return key
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());
    },
    // Ficha de edición del alumno/docente resuelto, para poder chequearlo
    // antes de aprobar o de elegir entre candidatos ambiguos (KB-907). Sin
    // bookuserId (target NOT_FOUND) no hay a dónde linkear.
    targetProfileRoute(bookuserId) {
      return userProfileRoute(this.action.actionType, bookuserId);
    },
    birthDateLabel(birthDate) {
      return formatBirthDate(birthDate, 's/d');
    },
    toggleTarget(id) {
      this.checkedTargetIds = this.checkedTargetIds.includes(id)
        ? this.checkedTargetIds.filter((tid) => tid !== id)
        : [...this.checkedTargetIds, id];
    },
    onApprove() {
      const targetIds = this.allEligibleChecked ? undefined : [...this.checkedTargetIds];
      this.$emit('approve', { targetIds });
    },
    onPick(target) {
      this.$emit('pick-candidate', { targetId: target.id, bookuserId: this.pickChoice[target.id] });
    },
    async onSaveDraft() {
      this.savingDraft = true;
      try {
        await supportRequestService.updateReplyDraft(
          this.action.supportRequest.id,
          this.draftText,
        );
        this.$emit('reply-draft-saved', this.draftText);
        this.snackbarSuccess(this.$t('supportRequests.success.draftSaved'));
      } catch (_error) {
        this.snackbarError(this.$t('supportRequests.errors.saveDraft'));
      } finally {
        this.savingDraft = false;
      }
    },
  },
};
</script>
